"use client";

import Image from "next/image";
import Link from "next/link";

/* Path dihitung dari app/layanan/ - tempat berkas INI berada.
     ../components, ../i18n, ../data  -> naik satu tingkat ke app/ */
import ContactBanner from "../components/ContactBanner";
import Footer from "../components/Footer";
import { useLanguage } from "../i18n/LanguageProvider";
import { SERVICES, layananHref } from "../data/layanan";

/* ============================================================
   HALAMAN INDEKS LAYANAN - /layanan

   Menampilkan KELIMA BELAS layanan. Beranda hanya menampilkan tujuh;
   di sinilah sisanya berada, dan inilah yang membuat tombol "Lihat
   semua layanan" menepati namanya.

   ============================================================
   KARTU YANG BELUM PUNYA HALAMAN TIDAK DIJADIKAN TAUTAN

   Sebelas dari lima belas layanan belum punya app/layanan/{slug}/.
   Menautkan semuanya berarti halaman ini memajang sebelas 404 dalam
   satu grid - kerusakan yang lebih terlihat daripada sebelum halaman
   ini ada.

   Jadi kartu dibagi dua bentuk berdasarkan penanda `siap`:

     siap: true  -> <Link>, punya efek hover, ada baris "Selengkapnya"
     siap: false -> <div> biasa, tanpa efek hover, tanpa baris itu

   Layanannya TETAP TAMPIL dengan nama dan deskripsi lengkap. Yang
   ditahan hanya tautannya. Pengunjung tetap tahu layanan itu ada -
   hanya belum ada halaman untuk dibaca, dan itu jauh lebih baik
   daripada mendarat di 404.

   Perbedaannya sengaja HALUS, bukan lencana "segera hadir" yang
   mencolok. Halaman ini tentang layanan rumah sakit, bukan tentang
   kemajuan pengerjaan situsnya.

   Begitu satu halaman selesai ditulis, ubah `siap` jadi true di
   app/data/layanan.js dan kartunya berubah jadi tautan dengan
   sendirinya - tidak ada yang perlu disunting di berkas ini.

   ============================================================
   >>> JANGAN TULIS className SEBAGAI STRING MULTI-BARIS. <<<
   Proyek ini berakhiran baris CRLF. String class yang dipecah beberapa
   baris ikut membawa \r\n ke dalam nilai atribut dan memicu hydration
   error. Kalau perlu dipecah, pakai ["...", "..."].join(" ").
   ============================================================ */

/* Padding atas = tinggi header fixed, angkanya disalin dari
   app/page.jsx. Kalau tinggi di Header.jsx berubah, kedua berkas ini
   dan keempat halaman layanan lain harus ikut diubah. */
const PADDING_HEADER = "pt-[64px] md:pt-[112px]";

export default function LayananContent() {
  const { t } = useLanguage();

  return (
    <div className={`relative w-full overflow-x-hidden bg-[var(--c-surface)] ${PADDING_HEADER}`}>

      {/* ============================================================
         BARIS BREADCRUMB

         DISAMAKAN DENGAN HALAMAN LAIN. Versi sebelumnya berbeda sendiri:
         breadcrumb-nya berada di dalam <section> di atas latar putih,
         dengan pemisah berupa karakter garis miring.

         Enam halaman lain - IGD, MCU, Laboratorium, Swab & PCR, About,
         Gallery, dan Tentang Kami - semuanya memakai bentuk yang ada di
         bawah ini: pita #f1faf5 selebar layar dengan garis bawah, teks
         13px, dan pemisah panah SVG. Halaman indeks yang berbeda
         sendiri membuat pengunjung merasa berpindah ke situs lain.

         TIGA HAL YANG IKUT BERUBAH, dan ketiganya disengaja:

         1. <ol>, bukan rangkaian elemen sejajar. Urutan breadcrumb
            bermakna, dan daftar berurutan itulah yang memberi tahu
            pembaca layar "item 1 dari 2".

         2. aria-current="page" pada item terakhir. Itu yang menyatakan
            "Anda sedang di sini" - bukan cetak tebalnya, yang hanya
            terlihat oleh mata.

         3. Pemisahnya SVG ber-aria-hidden, BUKAN karakter "/". Kalau
            ditulis sebagai teks, pembaca layar membacakan "garis
            miring" di antara tiap item.
         ============================================================ */}
      <div className="border-b border-[var(--c-line)] bg-[var(--c-tint)]">
        <div className="mx-auto max-w-[1600px] px-4 py-3 sm:px-6 sm:py-3.5 lg:px-10">
          <nav aria-label="Breadcrumb" className="text-[13px] sm:text-sm">
            <ol className="flex flex-wrap items-center gap-y-1">
              <li className="flex items-center">
                <Link href="/" className="rounded text-[var(--c-muted)] underline-offset-4 transition-colors hover:text-[var(--c-brand)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)]">
                  {t("header.nav.home")}
                </Link>
              </li>

              <li className="flex items-center">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="mx-2 h-3.5 w-3.5 shrink-0 text-[var(--c-faint)] sm:mx-2.5">
                  <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>

                {/* header.menu.layanan.label, BUKAN beranda.layananJudul.

                   Keduanya bernilai "Fasilitas & Layanan", jadi di layar
                   hasilnya sama. Tapi keempat halaman layanan lain
                   memakai kunci yang pertama di breadcrumb-nya, dan satu
                   kunci untuk satu tempat berarti mengubah nama menu
                   nanti cukup di satu baris. */}
                <span aria-current="page" className="font-bold text-[var(--c-brand)]">
                  {t("header.menu.layanan.label")}
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* pt dikecilkan dari pt-16 ke pt-14 di md karena pita breadcrumb
         di atas sudah menyumbang jarak tersendiri. */}
      <section className="px-4 pb-16 pt-10 sm:px-6 md:pb-24 md:pt-14 lg:px-10">
        <div className="mx-auto max-w-[1600px]">

          <div className="mb-8 max-w-3xl sm:mb-10 md:mb-14">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[var(--c-brand-mid)] sm:mb-3 sm:text-xs sm:tracking-[0.18em] md:text-sm">
              {t("beranda.layananEyebrow")}
            </p>

            {/* h1, bukan h2. Ini judul halaman, dan tiap halaman hanya
               boleh punya satu h1. Di beranda judul yang sama berupa h2
               karena h1 di sana milik hero. */}
            <h1 className="mb-3 text-2xl font-bold leading-[1.1] tracking-tight text-[var(--c-ink)] sm:text-3xl md:mb-5 md:text-5xl">
              {t("beranda.layananJudul")}
            </h1>

            {/* HANYA t(), tanpa kalimat yang ditulis langsung di JSX.
               Di app/page.jsx ada kalimat "dalam satu tempat. Pilih
               layanan untuk melihat detailnya." yang ditulis mentah
               setelah pemanggilan t() yang sama, padahal kalimat itu
               sudah termuat di nilai terjemahannya - itulah sebabnya
               paragraf di beranda tercetak dobel. Jangan tiru di sini,
               dan hapus baris itu di sana. */}
            <p className="text-sm leading-relaxed text-[var(--c-body)] sm:text-base md:text-lg">
              {t("beranda.layananSub")}
            </p>
          </div>

          {/* Empat kolom baru di xl (1280px). Di lg, kartu selebar itu
             menyisakan judul dua kata di atas foto 4:3 yang tinggi -
             tiga kolom memberi proporsi yang lebih wajar. */}
          <ul className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4 xl:gap-8">
            {SERVICES.map((service) => (
              <li key={service.key} className="h-full">
                <KartuLayanan service={service} t={t} />
              </li>
            ))}
          </ul>

        </div>
      </section>

      <section id="kontak" className="scroll-mt-24 md:scroll-mt-32">
        <ContactBanner />
      </section>

      <Footer />

    </div>
  );
}

/* ============================================================
   KARTU

   Isinya identik untuk kedua bentuk; yang berbeda hanya
   pembungkusnya. Isinya dipisah ke Isi() supaya tidak ditulis dua
   kali - dua salinan yang sama akan menyimpang begitu salah satunya
   disunting.
   ============================================================ */

function KartuLayanan({ service, t }) {
  const dasar =
    "flex h-full flex-col overflow-hidden rounded-2xl bg-[var(--c-surface)] ring-1 ring-[var(--c-line)]";

  if (!service.siap) {
    /* Bukan tautan: tanpa efek hover dan tanpa fokus keyboard, karena
       tidak ada yang bisa dilakukan di sini. Elemen yang menyala saat
       disentuh kursor tapi tidak merespons klik terbaca sebagai rusak. */
    return (
      <div className={dasar}>
        <Isi service={service} t={t} bisaDiklik={false} />
      </div>
    );
  }

  return (
    <Link
      href={layananHref(service.slug)}
      className={`group ${dasar} transition duration-300 hover:ring-[var(--c-brand)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--c-brand-mid)] motion-reduce:transition-none`}
    >
      <Isi service={service} t={t} bisaDiklik={true} />
    </Link>
  );
}

function Isi({ service, t, bisaDiklik }) {
  const Icon = service.Icon;

  return (
    <>
      <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-gradient-to-br from-[var(--c-tint)] to-[var(--c-line-soft)]">
        {service.image ? (
          /* alt kosong, dan itu disengaja: nama layanan sudah tertulis
             sebagai teks tepat di bawah foto. Mengisi alt di sini
             membuat pembaca layar mengucapkan nama yang sama dua kali. */
          <Image
            src={service.image}
            fill
            sizes="(min-width: 1280px) 380px, (min-width: 1024px) 30vw, (min-width: 640px) 45vw, 92vw"
            alt=""
            className={`object-cover ${bisaDiklik ? "transition-transform duration-500 ease-out group-hover:scale-105 motion-reduce:transition-none" : ""}`}
          />
        ) : (
          <Icon size={72} strokeWidth={1.25} className="text-[var(--c-brand-mid)] opacity-70" aria-hidden="true" />
        )}

        {service.urgent && (
          <span className="absolute left-3 top-3 rounded-full bg-amber-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-900">
            {t("layananList.darurat")}
          </span>
        )}
      </div>

      {/* flex-1 pada paragraf mendorong baris "Selengkapnya" ke dasar
         kartu, sehingga semua kartu punya baris penutup yang sejajar
         meski panjang deskripsinya berbeda-beda. */}
      <div className="flex flex-1 flex-col p-5 md:p-6">
        <h2 className={`mb-2 text-base font-bold leading-snug text-[var(--c-ink)] md:text-lg ${bisaDiklik ? "transition-colors duration-300 group-hover:text-[var(--c-brand)] motion-reduce:transition-none" : ""}`}>
          {t(`header.layanan.${service.key}`)}
        </h2>

        <p className="flex-1 text-sm leading-relaxed text-[var(--c-body)]">
          {t(`layananList.${service.key}Desc`)}
        </p>

        {/* span, BUKAN Link. Tautan di dalam tautan adalah HTML tidak
           sah dan membingungkan pembaca layar. Kartunya sendiri sudah
           jadi tautan; ini hanya penanda visual bahwa ia bisa diklik. */}
        {bisaDiklik && (
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--c-brand)] underline-offset-4 group-hover:underline">
            {t("umum.selengkapnya")}
            <span aria-hidden="true" className="transition-transform duration-300 group-hover:translate-x-1 motion-reduce:transition-none">&rarr;</span>
          </span>
        )}
      </div>
    </>
  );
}
